import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReportTableRowActions, {
  REPORT_TABLE_ACTIONS_COLUMN,
  isReportTableActionsColumn,
} from "./ReportTableRowActions";
import "./ReportTable.scss";

/** Canonical rows-per-page choices — every ReportTable footer uses this list. */
const DEFAULT_PAGE_SIZES = [10, 25, 50, 100, "all"];
const NUMERIC_PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_FROZEN_WIDTH_REM = 12;
const DEFAULT_PINNED_RIGHT_WIDTH_REM = 6.5;
const DEFAULT_ACTIONS_COLUMN_WIDTH_REM = 2.75;
/** Soft ceiling for flexible text columns (titles, names) — stops one long nowrap cell dominating. */
const FLEX_COLUMN_CONTENT_MAX_REM = 20;
/** Absolute ceiling when a text column absorbs leftover pane width. */
const FLEX_COLUMN_GROW_MAX_REM = 36;
const LAYOUT_STORAGE_VERSION = 2;
/** Ignore sub-pixel / rounding noise when deciding whether widths changed. */
const WIDTH_CHANGE_TOLERANCE_PX = 1;

/** Field keys that are usually badge / enum / short control — never absorb leftover table width. */
const COMPACT_FIELD_RE =
  /^(status|mfa|state|active|inactive|enabled|disabled|archived|gender|flag|onoff|is[A-Z]|qty|quantity|count|amount|total|balance|diff|difference|available|expected|physical|batchcount|kitcount|version|rev|revision|id)$/i;
const COMPACT_LABEL_RE =
  /^(status|mfa|state|active|type|role|gender|qty|quantity|count|amount|available|expected|physical|version)$/i;
const COMPACT_VALUE_RE =
  /^(active|inactive|on|off|yes|no|true|false|enabled|disabled|male|female|n\/?a|—|-)$/i;
/** Text-heavy columns prefer leftover space when the pane is wider than content. */
const TEXT_HEAVY_FIELD_RE =
  /(name|email|title|description|department|role|study|product|imp|notes|comment|address|organisation|organization|reference|subject|document|filename|path|reason|detail)/i;
const TEXT_HEAVY_LABEL_RE =
  /^(name|user|email|title|description|department|role|study|product|notes|comment|organisation|organization|document|file name|file)$/i;

const defaultRenderCell = (row, field) => String(row[field] ?? "—");

/** Stable empty defaults — avoid `prop = []` creating a new array every render. */
const EMPTY_ROWS = Object.freeze([]);
const EMPTY_FIELD_OPTIONS = Object.freeze([]);
const EMPTY_DEFAULT_COLUMNS = Object.freeze([]);
const EMPTY_STRING_ARRAY = Object.freeze([]);

function sameStringArray(a, b) {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  if (left === right) return true;
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (String(left[i]) !== String(right[i])) return false;
  }
  return true;
}

export { REPORT_TABLE_ACTIONS_COLUMN, isReportTableActionsColumn, ReportTableRowActions };

function getRemPx() {
  return (
    parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16
  );
}

/** Truly fixed columns — selection + right-pinned/actions. Frozen columns keep measured widths. */
function isHardFixedColumnTh(th) {
  if (!th) return false;
  return (
    th.classList.contains("report-table__selection-column") ||
    th.classList.contains("is-pinned-right")
  );
}

function parseCssLengthToPx(value, remPx) {
  const raw = String(value || "").trim();
  if (!raw || raw === "none" || raw === "auto") return null;
  if (raw.endsWith("px")) {
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (raw.endsWith("rem")) {
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? n * remPx : null;
  }
  if (raw.endsWith("em")) {
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? n * remPx : null;
  }
  return null;
}

/** Optional fieldOption sizes: number = rem, or `"12rem"` / `"120px"`. */
function optionSizeToPx(value, remPx) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.ceil(value * remPx);
  }
  const fromCss = parseCssLengthToPx(value, remPx);
  if (fromCss != null) return Math.ceil(fromCss);
  const n = Number(value);
  return Number.isFinite(n) ? Math.ceil(n * remPx) : null;
}

function widthsEffectivelyEqual(a, b, tolerance = WIDTH_CHANGE_TOLERANCE_PX) {
  if (!a?.length || !b?.length || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (Math.abs((a[i] || 0) - (b[i] || 0)) > tolerance) return false;
  }
  return true;
}

/**
 * Visible/content width for a cell probe.
 * Avoids scrollWidth of ellipsized/nowrap text (e.g. long filenames) inflating columns.
 */
function measureProbeWidthPx(probe, remPx, { applyFlexCap = false } = {}) {
  if (!probe) return 0;

  const style = window.getComputedStyle(probe);
  const rectW = Math.ceil(probe.getBoundingClientRect().width || 0);
  const clientW = Math.ceil(probe.clientWidth || 0);
  const scrollW = Math.ceil(probe.scrollWidth || 0);
  const cssMax = parseCssLengthToPx(style.maxWidth, remPx);

  const truncating =
    style.textOverflow === "ellipsis" ||
    style.overflowX === "hidden" ||
    style.overflow === "hidden" ||
    (style.whiteSpace === "nowrap" && scrollW > Math.max(clientW, rectW) + 1);

  let width;
  if (truncating && Math.max(clientW, rectW) > 0) {
    // Prefer painted width when text is clipped; scrollWidth is the full untruncated string.
    width = Math.max(clientW, rectW);
  } else {
    width = Math.max(scrollW, rectW, clientW);
  }

  if (cssMax != null && Number.isFinite(cssMax) && cssMax > 0) {
    width = Math.min(width, Math.ceil(cssMax));
  }
  if (applyFlexCap) {
    width = Math.min(width, Math.ceil(FLEX_COLUMN_CONTENT_MAX_REM * remPx));
  }
  return Math.ceil(width);
}

/**
 * Intrinsic width from label + cell content + header chrome.
 * Ignores stretched `width:100%` filter inputs so the last column
 * before pinned Actions does not inherit viewport leftover space.
 */
function estimateColumnContentWidthPx(th, td, remPx, { applyFlexCap = false } = {}) {
  const labelEl = th.querySelector(".report-table__column-label-text");
  const labelW = labelEl ? Math.ceil(labelEl.scrollWidth) : 0;

  let cellW = 0;
  if (td) {
    const probe =
      td.querySelector(
        ".badge, .status-badge, span, button, a, .form-check, input, select",
      ) || td;
    cellW = measureProbeWidthPx(probe, remPx, { applyFlexCap });
  }

  const headActions = th.querySelector(".report-table__column-actions");
  const actionsW = headActions
    ? Math.ceil(headActions.getBoundingClientRect().width)
    : Math.ceil(3.1 * remPx);
  const dragW = Math.ceil(1.15 * remPx);
  const padX = Math.ceil(1.35 * remPx);

  const filterSlot = th.querySelector(".report-table__column-filter-slot");
  const filterControl = th.querySelector(
    ".report-table__column-search, .report-table__column-filter-slot input, .report-table__column-filter-slot select, .badge-multi-select, .aq-date-range-filter",
  );
  const hasFilter = Boolean(filterControl);
  let filterFloor = 0;
  if (hasFilter) {
    const softFloor = Math.ceil(5.5 * remPx);
    const richFilter = Boolean(
      th.querySelector(".badge-multi-select, .aq-date-range-filter"),
    );
    const measuredFilter = filterControl
      ? Math.ceil(filterControl.getBoundingClientRect().width) + Math.ceil(0.5 * remPx)
      : 0;
    const slotW = filterSlot
      ? Math.ceil(filterSlot.getBoundingClientRect().width)
      : 0;
    filterFloor = Math.max(
      softFloor,
      measuredFilter,
      richFilter ? Math.ceil(7.25 * remPx) : 0,
      // Prefer measured control over a stretched 100%-width input slot.
      slotW > 0 && slotW < softFloor * 2.5 ? slotW : 0,
    );
  }

  let width = Math.max(
    labelW + dragW + actionsW + padX,
    cellW + padX,
    filterFloor,
    Math.ceil(5.5 * remPx),
  );

  if (applyFlexCap) {
    // Header chrome can exceed the content soft-cap slightly; keep a modest headroom.
    const flexMax = Math.ceil((FLEX_COLUMN_CONTENT_MAX_REM + 2.5) * remPx);
    width = Math.min(width, flexMax);
  }

  return width;
}

function isTextHeavyColumn(fieldKey, labelText, fieldOption) {
  if (fieldOption?.grow != null && Number(fieldOption.grow) > 0) return true;
  if (fieldOption?.compact === true) return false;
  const key = String(fieldKey || "");
  const label = String(labelText || "").trim();
  return TEXT_HEAVY_FIELD_RE.test(key) || TEXT_HEAVY_LABEL_RE.test(label);
}

function isCompactFlexColumn(th, td, contentWidthPx, remPx, fieldOption) {
  if (isHardFixedColumnTh(th)) return false;
  if (fieldOption?.compact === true) return true;
  if (fieldOption?.grow != null && Number(fieldOption.grow) > 0) return false;

  const fieldKey = String(th.getAttribute("data-field") || "");
  const labelText = String(
    th.querySelector(".report-table__column-label-text")?.textContent || "",
  ).trim();
  const cellText = String(td?.textContent || "")
    .replace(/\s+/g, " ")
    .trim();

  if (COMPACT_FIELD_RE.test(fieldKey) || COMPACT_LABEL_RE.test(labelText)) {
    return true;
  }
  if (cellText && COMPACT_VALUE_RE.test(cellText) && labelText.length <= 32) {
    return true;
  }
  if (
    contentWidthPx <= 9.5 * remPx &&
    labelText.length <= 24 &&
    cellText.length <= 18 &&
    !isTextHeavyColumn(fieldKey, labelText, fieldOption)
  ) {
    return true;
  }
  return false;
}

/**
 * Turn raw getBoundingClientRect widths into content-aware preferred widths.
 * Deflates columns the browser inflated to fill space before sticky Actions.
 */
function refineMeasuredColumnWidths(rawWidths, headCells, bodyRow, fieldOptionsByKey) {
  const remPx = getRemPx();
  const compactFlags = [];
  const flexMaxPx = Math.ceil((FLEX_COLUMN_CONTENT_MAX_REM + 2.5) * remPx);

  const widths = rawWidths.map((raw, index) => {
    const th = headCells[index];
    if (!th || isHardFixedColumnTh(th)) {
      compactFlags[index] = false;
      return raw;
    }
    const fieldKey = String(th.getAttribute("data-field") || "");
    const fieldOption = fieldOptionsByKey?.get?.(fieldKey) || null;
    const explicitPreferred = optionSizeToPx(fieldOption?.preferredWidth, remPx);

    const td = bodyRow?.children?.[index];
    const contentProbe = estimateColumnContentWidthPx(th, td, remPx, {
      applyFlexCap: false,
    });
    const compact = isCompactFlexColumn(th, td, contentProbe, remPx, fieldOption);
    compactFlags[index] = compact;

    if (explicitPreferred != null) {
      return Math.ceil(explicitPreferred);
    }

    const contentW = estimateColumnContentWidthPx(th, td, remPx, {
      applyFlexCap: !compact,
    });

    if (compact) {
      // Keep label/filter readable; never pad with leftover viewport space.
      return Math.ceil(Math.max(contentW, Math.ceil(5.5 * remPx)));
    }

    const measured = Number(raw) || 0;
    const inflated =
      measured > contentW * 1.35 && measured - contentW > 1.5 * remPx;
    const next = inflated
      ? Math.ceil(contentW)
      : Math.ceil(Math.max(Math.min(measured, flexMaxPx), contentW));
    return Math.min(next, flexMaxPx);
  });

  return { widths, compactFlags };
}

/**
 * Build min / preferred / max / grow specs for each column from preferred widths + DOM.
 */
function buildColumnWidthSpecs(
  preferredWidths,
  headCells,
  bodyRow,
  compactFlags,
  fieldOptionsByKey,
) {
  const remPx = getRemPx();
  const baseMinPx = Math.ceil(5.5 * remPx);
  const flexGrowMaxPx = Math.ceil(FLEX_COLUMN_GROW_MAX_REM * remPx);
  const compactMaxPx = Math.ceil(12 * remPx);

  return preferredWidths.map((preferredRaw, index) => {
    const th = headCells?.[index];
    const preferred = Math.max(1, Math.ceil(Number(preferredRaw) || baseMinPx));
    const fieldKey = String(th?.getAttribute("data-field") || "");
    const fieldOption = fieldOptionsByKey?.get?.(fieldKey) || null;
    const td = bodyRow?.children?.[index];
    const hardFixed = isHardFixedColumnTh(th);
    const compact = compactFlags?.[index] === true;

    const optMin = optionSizeToPx(fieldOption?.minWidth, remPx);
    const optMax = optionSizeToPx(fieldOption?.maxWidth, remPx);
    const optGrow =
      fieldOption?.grow != null && Number.isFinite(Number(fieldOption.grow))
        ? Number(fieldOption.grow)
        : null;

    if (hardFixed) {
      return {
        preferred,
        min: preferred,
        max: preferred,
        grow: 0,
        fixed: true,
      };
    }

    // Usable minimum: header chrome + filter must remain operable.
    const contentMin = estimateColumnContentWidthPx(th, td, remPx, {
      applyFlexCap: false,
    });
    let min = Math.min(preferred, Math.max(baseMinPx, Math.ceil(contentMin * 0.85)));
    if (optMin != null) min = Math.max(baseMinPx, optMin);
    min = Math.min(min, preferred);

    let max;
    let grow;
    if (optMax != null) {
      max = Math.max(preferred, optMax);
    } else if (compact) {
      max = Math.max(preferred, Math.min(compactMaxPx, preferred + Math.ceil(1.5 * remPx)));
    } else {
      max = Math.max(preferred, flexGrowMaxPx);
    }

    if (optGrow != null) {
      grow = Math.max(0, optGrow);
    } else if (compact) {
      grow = 0;
    } else if (isTextHeavyColumn(fieldKey, th?.querySelector(".report-table__column-label-text")?.textContent, fieldOption)) {
      grow = 2;
    } else {
      grow = 1;
    }

    // Compact frozen columns stay put; text-heavy frozen columns may still grow.
    if (th?.classList.contains("is-frozen") && optGrow == null && compact) {
      grow = 0;
    }

    return {
      preferred,
      min,
      max: Math.max(max, preferred),
      grow,
      fixed: false,
    };
  });
}

/**
 * Deterministic pane fit:
 * - preferred total == available → use preferred
 * - preferred < available → grow by weight up to max
 * - preferred > available && mins fit → shrink by (preferred - min) capacity
 * - mins > available → use mins (horizontal scroll)
 */
function resolveColumnWidthsToPane(specs, paneWidth) {
  if (!paneWidth || !specs?.length) {
    return specs?.map((s) => s.preferred) || [];
  }

  const preferredTotal = specs.reduce((sum, s) => sum + s.preferred, 0);
  const minimumTotal = specs.reduce((sum, s) => sum + s.min, 0);

  if (minimumTotal > paneWidth) {
    return specs.map((s) => Math.ceil(s.min));
  }

  if (Math.abs(preferredTotal - paneWidth) <= WIDTH_CHANGE_TOLERANCE_PX) {
    return specs.map((s) => Math.ceil(s.preferred));
  }

  const widths = specs.map((s) => Math.ceil(s.preferred));

  if (preferredTotal < paneWidth) {
    let extra = paneWidth - preferredTotal;
    // Distribute among growable columns by weight; never beyond max.
    while (extra > 0) {
      const growers = specs
        .map((s, i) => ({ s, i }))
        .filter(({ s, i }) => s.grow > 0 && widths[i] < s.max);
      if (!growers.length) break;

      const weightSum = growers.reduce((sum, { s }) => sum + s.grow, 0);
      if (weightSum <= 0) break;

      let distributed = 0;
      const budget = extra;
      for (let order = 0; order < growers.length; order += 1) {
        const { s, i } = growers[order];
        const room = s.max - widths[i];
        if (room <= 0) continue;
        let share =
          order === growers.length - 1
            ? budget - distributed
            : Math.floor((budget * s.grow) / weightSum);
        share = Math.max(0, Math.min(share, room, budget - distributed));
        widths[i] += share;
        distributed += share;
      }

      if (distributed <= 0) break;
      extra -= distributed;
    }
    // Absorb leftover 1px rounding into the last growable column with room.
    if (extra > 0) {
      for (let i = specs.length - 1; i >= 0; i -= 1) {
        if (specs[i].grow > 0 && widths[i] < specs[i].max) {
          const add = Math.min(extra, specs[i].max - widths[i]);
          widths[i] += add;
          extra -= add;
          if (extra <= 0) break;
        }
      }
    }
    return widths;
  }

  // Shrink: capacity = preferred - min; never below min.
  let overflow = preferredTotal - paneWidth;
  while (overflow > 0) {
    const shrinkers = specs
      .map((s, i) => ({
        i,
        capacity: Math.max(0, widths[i] - s.min),
      }))
      .filter((x) => x.capacity > 0);
    if (!shrinkers.length) break;

    const capacitySum = shrinkers.reduce((sum, x) => sum + x.capacity, 0);
    let cutTotal = 0;
    const budget = overflow;
    for (let order = 0; order < shrinkers.length; order += 1) {
      const { i, capacity } = shrinkers[order];
      let cut =
        order === shrinkers.length - 1
          ? budget - cutTotal
          : Math.floor((budget * capacity) / capacitySum);
      cut = Math.max(0, Math.min(cut, capacity, budget - cutTotal));
      widths[i] -= cut;
      cutTotal += cut;
    }
    if (cutTotal <= 0) break;
    overflow -= cutTotal;
  }

  return widths;
}

function loadPersistedLayout(storageKey) {
  if (!storageKey) return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Accept v1 (columns only) and v2 (columns + page size + filters + sort).
    if (parsed?.v !== 1 && parsed?.v !== LAYOUT_STORAGE_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function savePersistedLayout(storageKey, layout) {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({ v: LAYOUT_STORAGE_VERSION, ...layout }),
    );
  } catch {
    // ignore quota / private mode errors
  }
}

function sanitizePersistedLayout(
  saved,
  {
    defaultColumns,
    fieldOptions,
    pinnedRightColumns,
    pageSizeOptions,
    defaultPageSize,
  },
) {
  const validFields = new Set(fieldOptions.map((option) => option.key));
  const filterValid = (cols) =>
    (Array.isArray(cols) ? cols : []).filter((col) => validFields.has(col));

  let selectedColumns = filterValid(saved?.selectedColumns);
  defaultColumns.forEach((col) => {
    if (selectedColumns.includes(col)) return;
    const defaultIndex = defaultColumns.indexOf(col);
    let insertAt = selectedColumns.length;
    for (let i = defaultIndex - 1; i >= 0; i -= 1) {
      const anchor = defaultColumns[i];
      const anchorIndex = selectedColumns.indexOf(anchor);
      if (anchorIndex >= 0) {
        insertAt = anchorIndex + 1;
        break;
      }
    }
    selectedColumns.splice(insertAt, 0, col);
  });
  if (!selectedColumns.length) selectedColumns = [...defaultColumns];

  const anchor = defaultColumns[0];
  if (anchor && selectedColumns.includes(anchor)) {
    selectedColumns = [anchor, ...selectedColumns.filter((col) => col !== anchor)];
  }

  let frozenColumns = filterValid(saved?.frozenColumns);
  if (anchor && !frozenColumns.includes(anchor)) {
    frozenColumns = [anchor, ...frozenColumns.filter((col) => col !== anchor)];
  }
  if (!frozenColumns.length && anchor) frozenColumns = [anchor];

  const unpinnedRightColumns = filterValid(saved?.unpinnedRightColumns).filter(
    (col) => pinnedRightColumns.includes(col),
  );

  const columnSearches = {};
  if (saved?.columnSearches && typeof saved.columnSearches === "object") {
    Object.entries(saved.columnSearches).forEach(([field, value]) => {
      if (!validFields.has(field)) return;
      const text = String(value ?? "").trim();
      if (text) columnSearches[field] = text;
    });
  }

  const sizeOptions =
    Array.isArray(pageSizeOptions) && pageSizeOptions.length
      ? pageSizeOptions
      : DEFAULT_PAGE_SIZES;
  const allowedPageSizes = new Set(sizeOptions);
  const fallbackPageSize = allowedPageSizes.has(defaultPageSize)
    ? defaultPageSize
    : allowedPageSizes.has(10)
      ? 10
      : sizeOptions[0];
  let pageSize = saved?.pageSize;
  if (pageSize === "all") {
    if (!allowedPageSizes.has("all")) pageSize = fallbackPageSize;
  } else {
    pageSize = Number(pageSize);
    if (!allowedPageSizes.has(pageSize)) {
      pageSize = fallbackPageSize;
    }
  }

  const sortField =
    saved?.sortField && validFields.has(saved.sortField) ? saved.sortField : "";
  const sortDirection = saved?.sortDirection === "desc" ? "desc" : "asc";

  return {
    selectedColumns,
    frozenColumns,
    unpinnedRightColumns,
    columnSearches,
    pageSize,
    sortField,
    sortDirection,
  };
}
const SKELETON_BAR_WIDTHS = [72, 58, 84, 64, 76, 52, 88, 68];
const SKELETON_ROW_MIN = 8;
/** Matches compact table-sm body rows so placeholders fill the pane without a gap. */
const SKELETON_ROW_HEIGHT_PX = 36;

function skeletonBarPercent(rowIndex, colIndex) {
  return SKELETON_BAR_WIDTHS[
    (rowIndex * 3 + colIndex) % SKELETON_BAR_WIDTHS.length
  ];
}

const ReportTable = ({
  rows = EMPTY_ROWS,
  fieldOptions = EMPTY_FIELD_OPTIONS,
  defaultColumns = EMPTY_DEFAULT_COLUMNS,
  renderCell = defaultRenderCell,
  getFieldLabel,
  /** When true, body shows skeleton rows instead of data / empty state. */
  loading = false,
  emptyMessage = "No matching records",
  emptyDescription = "No records match the current filters.",
  onExportExcel,
  exportLabel = "Export Excel",
  /** Rows-per-page choices. Defaults to `[10, 25, 50, 100, "all"]`. Omit `"all"` to block full render. */
  pageSizeOptions: pageSizeOptionsProp,
  /** Initial rows-per-page when no persisted layout (`number` or `"all"`). */
  defaultPageSize,
  frozenColumnWidthRem = DEFAULT_FROZEN_WIDTH_REM,
  /** Width for pinned-right columns. Defaults to 2.75rem when `rowActions` is set. */
  pinnedRightColumnWidthRem,
  serverPagination = null,
  columnSearchMode = "client",
  serverColumnSearch = null,
  pinnedRightColumns = EMPTY_STRING_ARRAY,
  hideAddColumnFields = EMPTY_STRING_ARRAY,
  hideColumnHeaderActionsFields = EMPTY_STRING_ARRAY,
  /** Column keys that never show a filter/search control (Actions always included). */
  hideColumnSearchFields = EMPTY_STRING_ARRAY,
  renderColumnSearch = null,
  matchColumnSearch = null,
  getColumnCellClassName = null,
  getColumnHeaderClassName = null,
  persistLayoutKey = "",
  onResetLayout,
  disableColumnLayout = false,
  tourIds = null,
  rowSelection = null,
  onFilteredRowsChange = null,
  onRowClick = null,
  getRowKey = null,
  selectedRowKey = "",
  /** Optional (row, rowIndex) => extra className(s) for the <tr>. */
  getRowClassName = null,
  /** Field keys that show a clickable sort control in the header. */
  sortableFields = [],
  /** Optional (row, field) => comparable value for sorting. */
  getSortValue = null,
  /**
   * Built-in compact Actions column (pinned right, no header title, fixed ⋮ button).
   * Array of `{ label, icon, onClick, visible?, disabled?, dividerBefore?, dataTour? }`.
   */
  rowActions = null,
  /**
   * Optional per-row props for built-in Actions: `(row, rowIndex) => ({ dataTour, forceShow, menuZIndex })`.
   */
  getRowActionsProps = null,
  /**
   * Fit locked column widths to the body pane so the table does not show a
   * horizontal scrollbar when columns can safely shrink to their usable mins.
   * Opt out with `fitToContainer={false}` for rare legacy layouts.
   */
  fitToContainer = true,
}) => {
  const isTableLoading = Boolean(loading || serverPagination?.loading);
  const hasBuiltInRowActions = Array.isArray(rowActions);
  const resolvedPinnedRightColumnWidthRem =
    pinnedRightColumnWidthRem ??
    (hasBuiltInRowActions ? DEFAULT_ACTIONS_COLUMN_WIDTH_REM : DEFAULT_PINNED_RIGHT_WIDTH_REM);

  const resolvedFieldOptions = useMemo(() => {
    if (!hasBuiltInRowActions) return fieldOptions;
    if (fieldOptions.some((option) => isReportTableActionsColumn(option.key))) {
      return fieldOptions;
    }
    return [
      ...fieldOptions,
      { key: REPORT_TABLE_ACTIONS_COLUMN, label: "Actions" },
    ];
  }, [fieldOptions, hasBuiltInRowActions]);

  const resolvedDefaultColumns = useMemo(() => {
    if (!hasBuiltInRowActions) return defaultColumns;
    if (defaultColumns.some((col) => isReportTableActionsColumn(col))) {
      return defaultColumns;
    }
    return [...defaultColumns, REPORT_TABLE_ACTIONS_COLUMN];
  }, [defaultColumns, hasBuiltInRowActions]);

  const resolvedPinnedRightColumns = useMemo(() => {
    const base = Array.isArray(pinnedRightColumns) ? [...pinnedRightColumns] : [];
    if (!hasBuiltInRowActions) return base;
    if (base.some((col) => isReportTableActionsColumn(col))) return base;
    return [...base, REPORT_TABLE_ACTIONS_COLUMN];
  }, [pinnedRightColumns, hasBuiltInRowActions]);

  const resolvedHideAddColumnFields = useMemo(() => {
    const set = new Set(hideAddColumnFields);
    if (hasBuiltInRowActions) set.add(REPORT_TABLE_ACTIONS_COLUMN);
    return [...set];
  }, [hideAddColumnFields, hasBuiltInRowActions]);

  const resolvedHideColumnHeaderActionsFields = useMemo(() => {
    const set = new Set(hideColumnHeaderActionsFields);
    if (hasBuiltInRowActions) set.add(REPORT_TABLE_ACTIONS_COLUMN);
    return [...set];
  }, [hideColumnHeaderActionsFields, hasBuiltInRowActions]);

  const [selectedColumns, setSelectedColumns] = useState(resolvedDefaultColumns);
  const [columnSearches, setColumnSearches] = useState({});
  const [draggedColumn, setDraggedColumn] = useState("");
  const [columnPickerIndex, setColumnPickerIndex] = useState(null);
  const [columnPickerPosition, setColumnPickerPosition] = useState({ top: 0, left: 0 });
  const pageSizeOptions =
    Array.isArray(pageSizeOptionsProp) && pageSizeOptionsProp.length
      ? pageSizeOptionsProp
      : DEFAULT_PAGE_SIZES;
  const resolvedDefaultPageSize = pageSizeOptions.includes(defaultPageSize)
    ? defaultPageSize
    : pageSizeOptions.includes(Number(defaultPageSize))
      ? Number(defaultPageSize)
      : pageSizeOptions.includes(10)
        ? 10
        : pageSizeOptions[0];
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(resolvedDefaultPageSize);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    if (pageSizeOptions.includes(tablePageSize)) return;
    setTablePageSize(10);
  }, [pageSizeOptions, tablePageSize]);
  const [frozenColumns, setFrozenColumns] = useState(
    disableColumnLayout
      ? []
      : resolvedDefaultColumns[0]
        ? [resolvedDefaultColumns[0]]
        : [],
  );
  const [unpinnedRightColumns, setUnpinnedRightColumns] = useState([]);
  const [layoutReady, setLayoutReady] = useState(!persistLayoutKey);
  const columnPickerRef = useRef(null);
  const headerSelectAllRef = useRef(null);
  const headPaneRef = useRef(null);
  const bodyPaneRef = useRef(null);
  const headTableRef = useRef(null);
  const bodyTableRef = useRef(null);
  const [skeletonRowCount, setSkeletonRowCount] = useState(SKELETON_ROW_MIN);
  /** Locked preferred px widths — avoid remasuring after row/action refreshes (prevents column grow). */
  const lockedColumnWidthsRef = useRef(null);
  /** Last committed resolved widths (after fit) — used for sticky offsets + ResizeObserver dedupe. */
  const lastResolvedWidthsRef = useRef(null);
  const lastWidthLayoutKeyRef = useRef("");
  const [widthSyncNonce, setWidthSyncNonce] = useState(0);
  /** Resolved widths for sticky CSS in React render (keeps head/body offsets after re-renders). */
  const [resolvedStickyWidths, setResolvedStickyWidths] = useState(null);
  const onServerColumnSearchRef = useRef(serverColumnSearch?.onChange);

  onServerColumnSearchRef.current = serverColumnSearch?.onChange;

  const fieldOptionsByKey = useMemo(() => {
    const map = new Map();
    (resolvedFieldOptions || []).forEach((option) => {
      if (option?.key != null) map.set(String(option.key), option);
    });
    return map;
  }, [resolvedFieldOptions]);

  const syncHeadHorizontalScroll = useCallback(() => {
    const head = headPaneRef.current;
    const body = bodyPaneRef.current;
    if (!head || !body) return;

    // Body vertical scrollbar shrinks clientWidth; pad head so both panes share
    // the same content viewport — otherwise columns drift while scrolling X.
    const scrollbarWidth = Math.max(0, body.offsetWidth - body.clientWidth);
    const pad = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "";
    if (head.style.paddingRight !== pad) {
      head.style.paddingRight = pad;
    }

    if (head.scrollLeft !== body.scrollLeft) {
      head.scrollLeft = body.scrollLeft;
    }
  }, []);

  const fitWidthsToContainer = useCallback(
    (preferredWidths) => {
      const paneWidth = Math.floor(bodyPaneRef.current?.clientWidth || 0);
      if (!paneWidth || !preferredWidths?.length) return preferredWidths;

      const headCells = [
        ...(headTableRef.current?.querySelectorAll("thead tr > *") || []),
      ];
      const bodyRow = [
        ...(bodyTableRef.current?.querySelectorAll("tbody tr") || []),
      ].find((row) => row.children.length === headCells.length);

      const remPx = getRemPx();
      const compactFlags = headCells.map((th, index) => {
        if (isHardFixedColumnTh(th)) return false;
        const fieldKey = String(th.getAttribute("data-field") || "");
        const fieldOption = fieldOptionsByKey.get(fieldKey) || null;
        const td = bodyRow?.children?.[index];
        const contentW = estimateColumnContentWidthPx(th, td, remPx, {
          applyFlexCap: false,
        });
        return isCompactFlexColumn(th, td, contentW, remPx, fieldOption);
      });

      const specs = buildColumnWidthSpecs(
        preferredWidths,
        headCells,
        bodyRow,
        compactFlags,
        fieldOptionsByKey,
      );
      return resolveColumnWidthsToPane(specs, paneWidth);
    },
    [fieldOptionsByKey],
  );

  const applyStickyOffsetsFromWidths = useCallback((widths) => {
    const headTable = headTableRef.current;
    const bodyTable = bodyTableRef.current;
    if (!headTable || !bodyTable || !widths?.length) return;

    const applyToRowCells = (cells) => {
      if (!cells?.length) return;
      let frozenLeftPx = 0;
      const pinnedRightIndices = [];
      cells.forEach((cell, index) => {
        if (cell.classList.contains("is-pinned-right")) {
          pinnedRightIndices.push(index);
        }
      });

      cells.forEach((cell, index) => {
        const w = widths[index];
        if (w == null) return;

        if (
          cell.classList.contains("is-frozen") ||
          cell.classList.contains("report-table__selection-column")
        ) {
          cell.style.setProperty("--frozen-left", `${frozenLeftPx}px`);
          cell.style.setProperty("--frozen-width", `${w}px`);
          frozenLeftPx += w;
        } else if (cell.classList.contains("is-pinned-right")) {
          const pinOrder = pinnedRightIndices.indexOf(index);
          let rightPx = 0;
          for (let i = pinOrder + 1; i < pinnedRightIndices.length; i += 1) {
            rightPx += widths[pinnedRightIndices[i]] || 0;
          }
          cell.style.setProperty("--frozen-right", `${rightPx}px`);
          cell.style.setProperty("--frozen-width", `${w}px`);
        }
      });
    };

    const headCells = headTable.querySelectorAll("thead tr > *");
    applyToRowCells([...headCells]);
    bodyTable.querySelectorAll("tbody tr").forEach((row) => {
      applyToRowCells([...row.children]);
    });
  }, []);

  const applySplitTableColumnWidths = useCallback(
    (preferredWidths) => {
      const headTable = headTableRef.current;
      const bodyTable = bodyTableRef.current;
      if (!headTable || !bodyTable || !preferredWidths?.length) return null;

      const widths = fitToContainer
        ? fitWidthsToContainer(preferredWidths)
        : preferredWidths;

      if (
        lastResolvedWidthsRef.current &&
        widthsEffectivelyEqual(lastResolvedWidthsRef.current, widths)
      ) {
        // Still refresh sticky vars after DOM row swaps.
        applyStickyOffsetsFromWidths(widths);
        syncHeadHorizontalScroll();
        return widths;
      }

      const totalWidthPx = widths.reduce((sum, value) => sum + value, 0);
      const applyWidths = (table) => {
        const cols = table.querySelectorAll("colgroup col");
        cols.forEach((col, index) => {
          if (widths[index] == null) return;
          const width = `${widths[index]}px`;
          col.style.width = width;
          col.style.minWidth = width;
          col.style.maxWidth = width;
        });
        // Fixed layout locks head/body to identical column geometry while scrolling.
        table.classList.add("is-width-locked");
        table.style.width = `${totalWidthPx}px`;
        table.style.minWidth = `${totalWidthPx}px`;
        table.style.maxWidth = `${totalWidthPx}px`;
      };

      applyWidths(headTable);
      applyWidths(bodyTable);
      applyStickyOffsetsFromWidths(widths);
      lastResolvedWidthsRef.current = widths;
      setResolvedStickyWidths((prev) =>
        widthsEffectivelyEqual(prev, widths) ? prev : widths,
      );
      syncHeadHorizontalScroll();
      return widths;
    },
    [
      fitToContainer,
      fitWidthsToContainer,
      applyStickyOffsetsFromWidths,
      syncHeadHorizontalScroll,
    ],
  );

  const measureSplitTableColumnWidths = useCallback(() => {
    const headTable = headTableRef.current;
    const bodyTable = bodyTableRef.current;
    if (!headTable || !bodyTable) return null;

    const headCells = [...headTable.querySelectorAll("thead tr > *")];
    if (!headCells.length) return null;

    const bodyRow = [...bodyTable.querySelectorAll("tbody tr")].find(
      (row) => row.children.length === headCells.length,
    );

    // Clear only while measuring a new layout — never on row/data refresh.
    const clearLockedWidth = (table) => {
      table.classList.remove("is-width-locked");
      table.querySelectorAll("colgroup col").forEach((col) => {
        col.style.width = "";
        col.style.minWidth = "";
        col.style.maxWidth = "";
      });
      table.style.width = "";
      table.style.minWidth = "";
      table.style.maxWidth = "";
    };
    clearLockedWidth(headTable);
    clearLockedWidth(bodyTable);
    lastResolvedWidthsRef.current = null;
    setResolvedStickyWidths(null);

    // Clear sticky width vars so measure is not forced to frozenColumnWidthRem.
    const clearFrozenVars = (cells) => {
      cells.forEach((cell) => {
        cell.style.removeProperty("--frozen-left");
        cell.style.removeProperty("--frozen-width");
        cell.style.removeProperty("--frozen-right");
      });
    };
    clearFrozenVars(headCells);
    bodyTable.querySelectorAll("tbody tr").forEach((row) => {
      clearFrozenVars([...row.children]);
    });

    // Shrink-wrap measure: avoid browser stuffing leftover viewport into the
    // last flex column before sticky/pinned Actions.
    headTable.classList.add("is-measuring");
    bodyTable.classList.add("is-measuring");

    const remPx = getRemPx();
    const remToPx = (rem) => Math.ceil(Number(rem) * remPx);
    const selectionRem = rowSelection?.columnWidthRem ?? 2.75;

    let rawWidths;
    try {
      rawWidths = headCells.map((th, index) => {
        // Pinned Actions / selection: lock to configured rem — never auto-grow from header chrome.
        if (th.classList.contains("report-table__selection-column")) {
          return remToPx(selectionRem);
        }
        if (th.classList.contains("is-pinned-right")) {
          if (th.classList.contains("report-table__col-actions")) {
            return remToPx(DEFAULT_ACTIONS_COLUMN_WIDTH_REM);
          }
          return remToPx(resolvedPinnedRightColumnWidthRem);
        }

        const td = bodyRow?.children[index];
        const fieldKey = String(th.getAttribute("data-field") || "");
        const fieldOption = fieldOptionsByKey.get(fieldKey) || null;
        const explicitPreferred = optionSizeToPx(fieldOption?.preferredWidth, remPx);
        if (explicitPreferred != null) {
          return explicitPreferred;
        }

        const labelEl = th.querySelector(".report-table__column-label-text");
        const labelText = String(labelEl?.textContent || "").trim();
        // Empty header title (Actions): width from menu cell only.
        if (!labelText && !th.classList.contains("is-frozen")) {
          const bodyW = td?.getBoundingClientRect().width || 0;
          return Math.max(Math.ceil(bodyW || remToPx(2.75)), remToPx(2.5));
        }

        // Frozen columns measure from content — never force every frozen col to frozenColumnWidthRem.
        if (th.classList.contains("is-frozen")) {
          const contentW = estimateColumnContentWidthPx(th, td, remPx, {
            applyFlexCap: true,
          });
          return Math.ceil(Math.max(contentW, remToPx(5.5)));
        }

        const headW = th.getBoundingClientRect().width;
        const bodyW = td?.getBoundingClientRect().width || 0;
        // Cap body contribution early so long nowrap cells cannot dominate raw measure.
        const cappedBodyW = Math.min(
          bodyW,
          remToPx(FLEX_COLUMN_CONTENT_MAX_REM + 2.5),
        );
        return Math.ceil(Math.max(headW, cappedBodyW));
      });
    } finally {
      headTable.classList.remove("is-measuring");
      bodyTable.classList.remove("is-measuring");
    }

    const { widths } = refineMeasuredColumnWidths(
      rawWidths,
      headCells,
      bodyRow,
      fieldOptionsByKey,
    );
    return widths;
  }, [
    resolvedPinnedRightColumnWidthRem,
    rowSelection?.columnWidthRem,
    fieldOptionsByKey,
  ]);

  const isServerColumnSearch = columnSearchMode === "server";
  const serverSearchableFieldSet = useMemo(
    () => new Set(serverColumnSearch?.searchableFields || []),
    [serverColumnSearch?.searchableFields],
  );

  const defaultColumnSet = useMemo(
    () => new Set(resolvedDefaultColumns),
    [resolvedDefaultColumns],
  );
  const hideAddColumnSet = useMemo(
    () => new Set(resolvedHideAddColumnFields),
    [resolvedHideAddColumnFields],
  );
  const hideColumnHeaderActionsSet = useMemo(
    () => new Set(resolvedHideColumnHeaderActionsFields),
    [resolvedHideColumnHeaderActionsFields],
  );

  const hideColumnSearchSet = useMemo(() => {
    const set = new Set(hideColumnSearchFields);
    // Actions column must never expose filter/search.
    set.add("rowActions");
    set.add("Actions");
    set.add("actions");
    return set;
  }, [hideColumnSearchFields]);
  const sortableFieldSet = useMemo(
    () => new Set(Array.isArray(sortableFields) ? sortableFields : []),
    [sortableFields],
  );
  const effectivePinnedRightColumns = useMemo(
    () =>
      resolvedPinnedRightColumns.filter((col) => !unpinnedRightColumns.includes(col)),
    [resolvedPinnedRightColumns, unpinnedRightColumns],
  );
  const pinnedRightSet = useMemo(
    () => new Set(effectivePinnedRightColumns),
    [effectivePinnedRightColumns],
  );
  const detailedColumns = selectedColumns.length
    ? selectedColumns
    : resolvedDefaultColumns;
  const pinnedRightInView = useMemo(
    () => detailedColumns.filter((col) => pinnedRightSet.has(col)),
    [detailedColumns, pinnedRightSet],
  );

  const resolveRowKey = (row, rowIndex) => {
    if (typeof getRowKey === "function") {
      return String(getRowKey(row, rowIndex) ?? rowIndex);
    }
    return String(row?.id ?? row?.AppointmentId ?? row?.appointmentId ?? rowIndex);
  };

  const showColumnSearchInput = (field) => {
    if (hideColumnSearchSet.has(field)) return false;
    if (renderColumnSearch?.(field, { checkOnly: true })) return true;
    const label = String(labelFor(field) || "").trim().toLowerCase();
    if (label === "actions" || label === "action") return false;
    if (!isServerColumnSearch) return true;
    if (!serverColumnSearch?.searchableFields?.length) return true;
    return serverSearchableFieldSet.has(field);
  };

  const renderSearchControl = (col) => {
    if (hideColumnSearchSet.has(col)) {
      return (
        <div className="report-table__column-filter-slot">
          <span className="report-table__column-filter-spacer" aria-hidden="true" />
        </div>
      );
    }

    const hasCustomSearch = Boolean(
      renderColumnSearch?.(col, { checkOnly: true }),
    );
    // Built-in row "Action(s)" columns stay blank unless a custom filter is provided.
    const label = String(labelFor(col) || "").trim().toLowerCase();
    if (!hasCustomSearch && (label === "actions" || label === "action")) {
      return (
        <div className="report-table__column-filter-slot">
          <span className="report-table__column-filter-spacer" aria-hidden="true" />
        </div>
      );
    }

    const custom = renderColumnSearch?.(col, {
      value: columnSearches[col] || "",
      onChange: (nextValue) =>
        setColumnSearches((prev) => ({ ...prev, [col]: nextValue })),
      disabled: isTableLoading,
      isServerColumnSearch,
    });

    let content = null;
    if (hasCustomSearch) {
      // Custom handler owns this column — never fall back to default search.
      content = custom || null;
    } else if (showColumnSearchInput(col)) {
      const fieldLabel = labelFor(col);
      content = (
        <input
          type="search"
          className="report-table__column-search"
          data-tour={tourIds?.filters?.[col] || undefined}
          placeholder={
            isServerColumnSearch
              ? `Search ${fieldLabel} (server)`
              : `Search ${fieldLabel}`
          }
          value={columnSearches[col] || ""}
          disabled={isTableLoading}
          onChange={(event) =>
            setColumnSearches((prev) => ({ ...prev, [col]: event.target.value }))
          }
        />
      );
    }

    return (
      <div className="report-table__column-filter-slot">
        {content || (
          <span className="report-table__column-filter-spacer" aria-hidden="true" />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (persistLayoutKey) return;
    setSelectedColumns((prev) =>
      sameStringArray(prev, resolvedDefaultColumns)
        ? prev
        : resolvedDefaultColumns,
    );
    setFrozenColumns((prev) => {
      const next = disableColumnLayout
        ? []
        : resolvedDefaultColumns[0]
          ? [resolvedDefaultColumns[0]]
          : [];
      return sameStringArray(prev, next) ? prev : next;
    });
    setUnpinnedRightColumns((prev) => (prev.length ? [] : prev));
    setColumnSearches((prev) =>
      Object.keys(prev || {}).length ? {} : prev,
    );
    setTablePage((prev) => (prev === 1 ? prev : 1));
  }, [resolvedDefaultColumns, disableColumnLayout, persistLayoutKey]);

  useEffect(() => {
    if (!persistLayoutKey) {
      setLayoutReady(true);
      return undefined;
    }

    setLayoutReady(false);
    const saved = loadPersistedLayout(persistLayoutKey);
    if (saved) {
      const sanitized = sanitizePersistedLayout(saved, {
        defaultColumns: resolvedDefaultColumns,
        fieldOptions: resolvedFieldOptions,
        pinnedRightColumns: resolvedPinnedRightColumns,
        pageSizeOptions,
        defaultPageSize: resolvedDefaultPageSize,
      });
      setSelectedColumns(sanitized.selectedColumns);
      setFrozenColumns(sanitized.frozenColumns);
      setUnpinnedRightColumns(sanitized.unpinnedRightColumns);
      setColumnSearches(sanitized.columnSearches || {});
      setTablePageSize(sanitized.pageSize);
      setSortField(sanitized.sortField || "");
      setSortDirection(sanitized.sortDirection || "asc");
    } else {
      setSelectedColumns(resolvedDefaultColumns);
      setFrozenColumns(
        disableColumnLayout
          ? []
          : resolvedDefaultColumns[0]
            ? [resolvedDefaultColumns[0]]
            : [],
      );
      setUnpinnedRightColumns([]);
      setColumnSearches({});
      setTablePageSize(resolvedDefaultPageSize);
      setSortField("");
      setSortDirection("asc");
    }
    setTablePage(1);
    setLayoutReady(true);
    // Load saved layout once per storage key (per user/browser).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistLayoutKey]);

  // rowActions often becomes available after permissions hydrate. Re-attach the
  // sticky Actions column so a refresh does not permanently drop it from layout.
  useEffect(() => {
    if (!layoutReady || !hasBuiltInRowActions) return;
    if (!resolvedFieldOptions.some((option) => isReportTableActionsColumn(option.key))) {
      return;
    }
    setSelectedColumns((prev) => {
      if (prev.some(isReportTableActionsColumn)) return prev;
      return [...prev, REPORT_TABLE_ACTIONS_COLUMN];
    });
  }, [layoutReady, hasBuiltInRowActions, resolvedFieldOptions]);

  useEffect(() => {
    if (!persistLayoutKey || !layoutReady) return undefined;
    // Avoid persisting a transient layout that is missing Actions while rowActions
    // is enabled (common race: permissions load after the first layout save).
    if (
      hasBuiltInRowActions &&
      !selectedColumns.some(isReportTableActionsColumn)
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      savePersistedLayout(persistLayoutKey, {
        selectedColumns,
        frozenColumns,
        unpinnedRightColumns,
        columnSearches,
        pageSize: tablePageSize,
        sortField,
        sortDirection,
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    persistLayoutKey,
    layoutReady,
    hasBuiltInRowActions,
    selectedColumns,
    frozenColumns,
    unpinnedRightColumns,
    columnSearches,
    tablePageSize,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (columnPickerIndex == null) return undefined;
    const onMouseDown = (event) => {
      if (columnPickerRef.current?.contains(event.target)) return;
      setColumnPickerIndex(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [columnPickerIndex]);

  const labelFor = (field) => {
    // Actions column: hide header title by default (compact ⋮ width).
    if (isReportTableActionsColumn(field)) {
      if (typeof getFieldLabel === "function") {
        const custom = getFieldLabel(field);
        if (custom !== undefined && custom !== null) return custom;
      }
      return "";
    }
    if (typeof getFieldLabel === "function") {
      const custom = getFieldLabel(field);
      // Allow "" so headers can hide title text.
      if (custom !== undefined && custom !== null) return custom;
    }
    return (
      resolvedFieldOptions.find((option) => option.key === field)?.label || field
    );
  };

  const renderTableCell = (row, field, rowIndex = 0) => {
    if (hasBuiltInRowActions && isReportTableActionsColumn(field)) {
      const extra =
        typeof getRowActionsProps === "function"
          ? getRowActionsProps(row, rowIndex) || {}
          : getRowActionsProps || {};
      return (
        <ReportTableRowActions row={row} actions={rowActions} {...extra} />
      );
    }
    return renderCell(row, field, rowIndex);
  };

  /** Hard rule: never allow center-align utility classes on ReportTable columns. */
  const resolveColumnClassName = (field, customClassName) =>
    [
      isReportTableActionsColumn(field) ? "report-table__col-actions" : "",
      ...String(customClassName || "")
        .split(/\s+/)
        .filter(
          (token) =>
            token &&
            !/^text-center$/i.test(token) &&
            !/^text-align-center$/i.test(token),
        ),
    ]
      .filter(Boolean)
      .join(" ");

  const filteredRows = useMemo(() => {
    if (isServerColumnSearch) return rows;
    const activeSearches = Object.entries(columnSearches)
      .map(([field, value]) => [field, String(value || "").trim().toLowerCase()])
      .filter(([, value]) => value);
    if (!activeSearches.length) return rows;
    return rows.filter((row) =>
      activeSearches.every(([field, value]) => {
        if (matchColumnSearch) {
          return matchColumnSearch(row, field, value);
        }
        return String(row[field] ?? "")
          .toLowerCase()
          .includes(value);
      }),
    );
  }, [rows, columnSearches, isServerColumnSearch, matchColumnSearch]);

  const sortedFilteredRows = useMemo(() => {
    if (!sortField || !sortableFieldSet.has(sortField)) return filteredRows;
    const dir = sortDirection === "desc" ? -1 : 1;
    const list = [...filteredRows];
    list.sort((left, right) => {
      const a =
        typeof getSortValue === "function"
          ? getSortValue(left, sortField)
          : left?.[sortField];
      const b =
        typeof getSortValue === "function"
          ? getSortValue(right, sortField)
          : right?.[sortField];
      if (typeof a === "number" && typeof b === "number") {
        return (a - b) * dir;
      }
      return (
        String(a ?? "").localeCompare(String(b ?? ""), undefined, {
          sensitivity: "base",
          numeric: true,
        }) * dir
      );
    });
    return list;
  }, [
    filteredRows,
    getSortValue,
    sortDirection,
    sortField,
    sortableFieldSet,
  ]);

  useEffect(() => {
    onFilteredRowsChange?.(sortedFilteredRows);
  }, [sortedFilteredRows, onFilteredRowsChange]);

  const hasRowSelection = Boolean(rowSelection);
  const selectionWidthRem = rowSelection?.columnWidthRem ?? 2.75;
  const getRowSelectionKey =
    rowSelection?.getRowKey ||
    ((row) => String(row?.id ?? row?.DocumentId ?? row?.capaId ?? ""));
  const isRowSelectable = rowSelection?.isRowSelectable || (() => true);
  const selectedKeySet = useMemo(() => {
    const raw = rowSelection?.selectedKeys;
    if (!raw) return new Set();
    if (raw instanceof Set) return raw;
    return new Set(raw.map((key) => String(key)));
  }, [rowSelection?.selectedKeys]);

  const buildFrozenStickyStyle = useCallback(
    (col, frozenIndex, rightFrozenIndex, isPinnedRight) => {
      const pinnedWidthRem = isReportTableActionsColumn(col)
        ? DEFAULT_ACTIONS_COLUMN_WIDTH_REM
        : resolvedPinnedRightColumnWidthRem;

      const widths = resolvedStickyWidths;
      const hasSelection = hasRowSelection;
      // Column index in the split table: selection (+1) + detailedColumns index.
      const colIndexInTable =
        detailedColumns.indexOf(col) + (hasSelection ? 1 : 0);

      if (widths?.length && frozenIndex >= 0) {
        let leftPx = 0;
        // Selection is index 0 when present; frozen columns follow in detailedColumns order.
        if (hasSelection) {
          leftPx += widths[0] || 0;
        }
        for (let i = 0; i < frozenIndex; i += 1) {
          const frozenCol = frozenColumns[i];
          const idx =
            detailedColumns.indexOf(frozenCol) + (hasSelection ? 1 : 0);
          leftPx += widths[idx] || 0;
        }
        const widthPx = widths[colIndexInTable];
        if (widthPx != null) {
          return {
            "--frozen-left": `${leftPx}px`,
            "--frozen-width": `${widthPx}px`,
          };
        }
      }

      if (widths?.length && isPinnedRight) {
        let rightPx = 0;
        for (let i = rightFrozenIndex + 1; i < pinnedRightInView.length; i += 1) {
          const pinCol = pinnedRightInView[i];
          const idx =
            detailedColumns.indexOf(pinCol) + (hasSelection ? 1 : 0);
          rightPx += widths[idx] || 0;
        }
        const widthPx = widths[colIndexInTable];
        if (widthPx != null) {
          return {
            "--frozen-right": `${rightPx}px`,
            "--frozen-width": `${widthPx}px`,
          };
        }
      }

      if (frozenIndex >= 0) {
        const leftRem =
          (hasRowSelection ? selectionWidthRem : 0) +
          frozenIndex * frozenColumnWidthRem;
        return {
          "--frozen-left": `${leftRem}rem`,
          "--frozen-width": `${frozenColumnWidthRem}rem`,
        };
      }
      if (isPinnedRight) {
        return {
          "--frozen-right": `${
            (pinnedRightInView.length - 1 - rightFrozenIndex) * pinnedWidthRem
          }rem`,
          "--frozen-width": `${pinnedWidthRem}rem`,
        };
      }
      return undefined;
    },
    [
      resolvedStickyWidths,
      hasRowSelection,
      detailedColumns,
      frozenColumns,
      pinnedRightInView,
      frozenColumnWidthRem,
      resolvedPinnedRightColumnWidthRem,
      selectionWidthRem,
    ],
  );

  const selectionStickyStyle = useMemo(() => {
    if (resolvedStickyWidths?.length) {
      return {
        "--frozen-left": "0px",
        "--frozen-width": `${resolvedStickyWidths[0]}px`,
      };
    }
    return {
      "--frozen-left": "0",
      "--frozen-width": `${selectionWidthRem}rem`,
    };
  }, [resolvedStickyWidths, selectionWidthRem]);

  useEffect(() => {
    if (!isServerColumnSearch || !onServerColumnSearchRef.current) return undefined;
    const debounceMs = serverColumnSearch?.debounceMs ?? 400;
    const timer = window.setTimeout(() => {
      onServerColumnSearchRef.current?.(columnSearches);
    }, debounceMs);
    return () => window.clearTimeout(timer);
  }, [columnSearches, isServerColumnSearch, serverColumnSearch?.debounceMs]);

  const isServerPaged = Boolean(serverPagination);
  const serverPage = serverPagination?.page ?? 1;
  const serverPageSize = serverPagination?.pageSize ?? tablePageSize;
  const serverTotalCount = serverPagination?.totalCount ?? sortedFilteredRows.length;

  const effectivePageSize = isServerPaged
    ? serverPageSize
    : tablePageSize === "all"
      ? Math.max(sortedFilteredRows.length, 1)
      : tablePageSize;
  const totalPages = isServerPaged
    ? Math.max(1, Math.ceil(serverTotalCount / Math.max(serverPageSize, 1)))
    : Math.max(1, Math.ceil(sortedFilteredRows.length / effectivePageSize));
  const activePage = isServerPaged ? serverPage : Math.min(Math.max(tablePage, 1), totalPages);
  const pageRows = useMemo(() => {
    if (isServerPaged) return sortedFilteredRows;
    const safePage = Math.min(Math.max(tablePage, 1), totalPages);
    const start = (safePage - 1) * effectivePageSize;
    return sortedFilteredRows.slice(start, start + effectivePageSize);
  }, [sortedFilteredRows, tablePage, effectivePageSize, totalPages, isServerPaged]);
  const showSkeletonRows = isTableLoading && pageRows.length === 0;

  const selectablePageRows = useMemo(
    () => (hasRowSelection ? pageRows.filter(isRowSelectable) : []),
    [hasRowSelection, pageRows, rowSelection?.isRowSelectable],
  );
  const pageSelectionKeys = useMemo(
    () => selectablePageRows.map(getRowSelectionKey).filter(Boolean),
    [selectablePageRows, rowSelection?.getRowKey],
  );
  const selectedOnPageCount = pageSelectionKeys.filter((key) => selectedKeySet.has(key)).length;
  const allPageRowsSelected =
    pageSelectionKeys.length > 0 && selectedOnPageCount === pageSelectionKeys.length;
  const somePageRowsSelected =
    selectedOnPageCount > 0 && selectedOnPageCount < pageSelectionKeys.length;

  const hasMoreFilteredThanPage =
    Boolean(rowSelection?.hasMoreFiltered) && !rowSelection?.allFilteredSelected;
  const headerShowsIndeterminate =
    somePageRowsSelected || (allPageRowsSelected && hasMoreFilteredThanPage);

  useEffect(() => {
    if (!headerSelectAllRef.current) return;
    headerSelectAllRef.current.indeterminate = headerShowsIndeterminate;
  }, [headerShowsIndeterminate]);

  useEffect(() => {
    rowSelection?.onContextChange?.({
      pageSelectableCount: pageSelectionKeys.length,
      pageSelectedCount: selectedOnPageCount,
      allPageSelected: allPageRowsSelected,
      filteredRowCount: sortedFilteredRows.length,
    });
  }, [
    pageSelectionKeys.length,
    selectedOnPageCount,
    allPageRowsSelected,
    sortedFilteredRows.length,
    rowSelection?.onContextChange,
  ]);

  const entryStart = isServerPaged
    ? serverTotalCount
      ? (activePage - 1) * serverPageSize + 1
      : 0
    : sortedFilteredRows.length
      ? (Math.min(tablePage, totalPages) - 1) * effectivePageSize + 1
      : 0;
  const entryEnd = isServerPaged
    ? Math.min(serverTotalCount, activePage * serverPageSize)
    : Math.min(sortedFilteredRows.length, Math.min(tablePage, totalPages) * effectivePageSize);
  const entryTotal = isServerPaged ? serverTotalCount : sortedFilteredRows.length;

  useEffect(() => {
    setTablePage(1);
  }, [columnSearches, effectivePageSize, sortField, sortDirection]);

  useEffect(() => {
    if (tablePage > totalPages) setTablePage(totalPages);
  }, [tablePage, totalPages]);

  const addColumn = (field, insertIndex) => {
    setSelectedColumns((prev) => {
      if (prev.includes(field)) return prev;
      const next = [...prev];
      const insertAt = Math.min(Math.max(insertIndex, frozenColumns.length), next.length);
      next.splice(insertAt, 0, field);
      return next;
    });
    setColumnPickerIndex(null);
  };

  const removeColumn = (field) => {
    if (defaultColumnSet.has(field)) return;
    setSelectedColumns((prev) => prev.filter((x) => x !== field));
    setFrozenColumns((prev) => prev.filter((x) => x !== field));
    setColumnSearches((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const moveColumnTo = (field, targetField) => {
    if (!field || !targetField || field === targetField) return;
    setSelectedColumns((prev) => {
      const fromIndex = prev.indexOf(field);
      const toIndex = prev.indexOf(targetField);
      const frozenCount = frozenColumns.length;
      if (fromIndex < frozenCount || toIndex < frozenCount) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const freezeColumn = (field) => {
    setSelectedColumns((prev) => {
      if (!prev.includes(field)) return prev;
      const nextFrozen = frozenColumns.includes(field) ? frozenColumns : [...frozenColumns, field];
      return [...nextFrozen, ...prev.filter((x) => !nextFrozen.includes(x))];
    });
    setFrozenColumns((prev) => (prev.includes(field) ? prev : [...prev, field]));
    setColumnPickerIndex(null);
  };

  const unfreezeColumn = (field) => {
    if (field === resolvedDefaultColumns[0]) return;
    setFrozenColumns((prev) => prev.filter((x) => x !== field));
  };

  const openColumnsAt = (insertIndex, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 248;
    const estimatedHeight = 340;
    const gap = 8;
    const viewportPadding = 12;
    const belowTop = rect.bottom + gap;
    const hasRoomBelow = belowTop + estimatedHeight <= window.innerHeight - viewportPadding;
    const top = hasRoomBelow
      ? belowTop
      : Math.max(viewportPadding, rect.top - estimatedHeight - gap);
    const left = Math.min(
      Math.max(viewportPadding, rect.left),
      window.innerWidth - width - viewportPadding,
    );
    setColumnPickerPosition({ top, left });
    setColumnPickerIndex((current) => (current === insertIndex ? null : insertIndex));
  };

  const resetLayout = () => {
    if (persistLayoutKey) {
      try {
        window.localStorage.removeItem(persistLayoutKey);
      } catch {
        // ignore
      }
    }
    setSelectedColumns(resolvedDefaultColumns);
    setFrozenColumns(resolvedDefaultColumns[0] ? [resolvedDefaultColumns[0]] : []);
    setUnpinnedRightColumns([]);
    setColumnSearches({});
    setTablePageSize(resolvedDefaultPageSize);
    setSortField("");
    setSortDirection("asc");
    setTablePage(1);
    lockedColumnWidthsRef.current = null;
    lastResolvedWidthsRef.current = null;
    setResolvedStickyWidths(null);
    lastWidthLayoutKeyRef.current = "";
    setWidthSyncNonce((n) => n + 1);
    onResetLayout?.();
  };

  const renderSplitColGroup = () => (
    <colgroup>
      {hasRowSelection ? <col /> : null}
      {detailedColumns.map((col) => (
        <col key={col} />
      ))}
    </colgroup>
  );

  /**
   * Measure once per column *structure*, then keep those px widths locked.
   * Re-measuring after action clicks / getAllUsers refresh clears cols →
   * table-layout:auto + width:max-content expands columns (horizontal scrollbar).
   */
  // Real body rows only — never lock widths from skeleton placeholders
  // (refresh measured skeletons → wide/uneven cols; Reset remasured real data).
  const hasBodyRows = pageRows.length > 0;
  const columnWidthLayoutKey = useMemo(
    () =>
      [
        detailedColumns.join("|"),
        frozenColumns.join("|"),
        pinnedRightInView.join("|"),
        hasRowSelection ? "sel" : "",
        String(frozenColumnWidthRem),
        String(resolvedPinnedRightColumnWidthRem),
        String(rowSelection?.columnWidthRem ?? ""),
      ].join("::"),
    [
      detailedColumns,
      frozenColumns,
      pinnedRightInView,
      hasRowSelection,
      frozenColumnWidthRem,
      resolvedPinnedRightColumnWidthRem,
      rowSelection?.columnWidthRem,
    ],
  );

  useLayoutEffect(() => {
    if (!layoutReady) return;

    const layoutChanged = lastWidthLayoutKeyRef.current !== columnWidthLayoutKey;
    if (layoutChanged) {
      lockedColumnWidthsRef.current = null;
      lastWidthLayoutKeyRef.current = columnWidthLayoutKey;
    }

    if (lockedColumnWidthsRef.current?.length) {
      // Re-apply after row/action refresh — never clear+remeasure (that grows columns).
      applySplitTableColumnWidths(lockedColumnWidthsRef.current);
      return;
    }

    // Wait for real data rows (not skeleton / loading placeholders).
    if (!hasBodyRows || showSkeletonRows || isTableLoading) return;

    const widths = measureSplitTableColumnWidths();
    if (!widths?.length) return;
    lockedColumnWidthsRef.current = widths;
    applySplitTableColumnWidths(widths);
  }, [
    layoutReady,
    hasBodyRows,
    showSkeletonRows,
    isTableLoading,
    columnWidthLayoutKey,
    widthSyncNonce,
    // Re-apply locked widths when body rows refresh (action / reload); do not remasure.
    pageRows,
    measureSplitTableColumnWidths,
    applySplitTableColumnWidths,
  ]);

  useEffect(() => {
    if (!fitToContainer || !layoutReady) return undefined;
    const pane = bodyPaneRef.current;
    if (!pane || typeof ResizeObserver === "undefined") return undefined;

    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (!lockedColumnWidthsRef.current?.length) return;
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = 0;
        applySplitTableColumnWidths(lockedColumnWidthsRef.current);
      });
    });
    observer.observe(pane);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [fitToContainer, layoutReady, applySplitTableColumnWidths]);

  useLayoutEffect(() => {
    if (!showSkeletonRows) {
      setSkeletonRowCount(SKELETON_ROW_MIN);
      return undefined;
    }

    const pane = bodyPaneRef.current;
    if (!pane) return undefined;

    const updateCount = () => {
      const height = pane.clientHeight;
      const next = Math.max(
        SKELETON_ROW_MIN,
        Math.floor(height / SKELETON_ROW_HEIGHT_PX) || SKELETON_ROW_MIN,
      );
      setSkeletonRowCount((prev) => (prev === next ? prev : next));
    };

    updateCount();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateCount);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [showSkeletonRows]);

  return (
    <div
      className={`report-table${fitToContainer ? " report-table--fit-container" : ""}`}
      data-tour={tourIds?.root || undefined}
    >
      <div className="report-table__frame">
        <div
          className="report-table__head-pane"
          ref={headPaneRef}
          aria-hidden={false}
        >
          <table
            ref={headTableRef}
            className="table table-sm report-table__table report-table__table--head"
          >
            {renderSplitColGroup()}
            <thead data-tour={tourIds?.thead || undefined}>
            <tr>
              {hasRowSelection ? (
                <th
                  className="report-table__selection-column is-frozen"
                  style={selectionStickyStyle}
                  aria-label="Select rows on this page"
                  title="Select all rows on this page only"
                >
                  <input
                    ref={headerSelectAllRef}
                    type="checkbox"
                    className="form-check-input report-table__row-select"
                    checked={allPageRowsSelected || Boolean(rowSelection?.allFilteredSelected)}
                    disabled={!pageSelectionKeys.length}
                    onChange={() => {
                      const headerChecked =
                        allPageRowsSelected || Boolean(rowSelection?.allFilteredSelected);
                      if (rowSelection.onToggleHeader) {
                        rowSelection.onToggleHeader(selectablePageRows, headerChecked);
                        return;
                      }
                      rowSelection.onTogglePage?.(selectablePageRows, !allPageRowsSelected);
                    }}
                    aria-label="Select all rows on this page only"
                    title="Selects only the rows visible on this page, not all search results"
                  />
                </th>
              ) : null}
              {detailedColumns.map((col, index) => {
                const label = labelFor(col);
                const isDefaultColumn = defaultColumnSet.has(col);
                const frozenIndex = frozenColumns.indexOf(col);
                const isFrozenColumn = frozenIndex >= 0;
                const isPrimaryFrozenColumn = col === resolvedDefaultColumns[0];
                const rightFrozenIndex = pinnedRightInView.indexOf(col);
                const isPinnedRight = rightFrozenIndex >= 0;
                const hideAddColumn = hideAddColumnSet.has(col) || isPinnedRight;
                const hideColumnHeaderActions =
                  disableColumnLayout ||
                  hideColumnHeaderActionsSet.has(col) ||
                  isReportTableActionsColumn(col);
                const showFreezeColumn = !disableColumnLayout && !isFrozenColumn && !isPinnedRight;
                const showUnfreezeColumn =
                  !disableColumnLayout && isFrozenColumn && !isPrimaryFrozenColumn;
                const showUnpinColumn =
                  !disableColumnLayout && isPinnedRight && !hideColumnHeaderActions;
                const isCompactHeader =
                  !String(label || "").trim() || isReportTableActionsColumn(col);
                const frozenStyle = buildFrozenStickyStyle(
                  col,
                  frozenIndex,
                  rightFrozenIndex,
                  isPinnedRight,
                );
                const headerExtraClass = resolveColumnClassName(
                  col,
                  getColumnHeaderClassName?.(col),
                );

                return (
                  <th
                    key={col}
                    data-field={col}
                    data-tour={tourIds?.columns?.[col] || undefined}
                    className={`report-table__smart-column ${draggedColumn === col ? "is-dragging" : ""} ${isFrozenColumn ? "is-locked is-frozen" : ""} ${isPinnedRight ? "is-pinned-right" : ""} ${isCompactHeader ? "is-compact-header" : ""} ${headerExtraClass}`.trim()}
                    style={frozenStyle}
                    draggable={!disableColumnLayout && !isFrozenColumn && !isPinnedRight && !isCompactHeader}
                    onDragStart={(event) => {
                      if (isFrozenColumn || isPinnedRight) return;
                      setDraggedColumn(col);
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", col);
                    }}
                    onDragOver={(event) => {
                      if (isFrozenColumn || isPinnedRight) return;
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(event) => {
                      if (isFrozenColumn || isPinnedRight) return;
                      event.preventDefault();
                      moveColumnTo(event.dataTransfer.getData("text/plain") || draggedColumn, col);
                      setDraggedColumn("");
                    }}
                    onDragEnd={() => setDraggedColumn("")}
                  >
                    <div
                      className={`report-table__column-head${
                        isCompactHeader ? " is-compact" : ""
                      }`}
                    >
                      {isCompactHeader ? (
                        <span className="report-table__column-label-text" aria-hidden="true" />
                      ) : (
                      <span
                        className={`report-table__column-drag-label${
                          sortableFieldSet.has(col) ? " is-sortable" : ""
                        }`}
                        title={
                          sortableFieldSet.has(col)
                            ? `Sort by ${label}`
                            : disableColumnLayout
                              ? label
                              : isFrozenColumn
                                ? "Frozen column"
                                : "Drag to reorder column"
                        }
                        data-tour={
                          tourIds?.dragColumn && col === tourIds?.dragColumnField
                            ? tourIds.dragColumn
                            : undefined
                        }
                        onClick={
                          sortableFieldSet.has(col)
                            ? () => {
                                if (sortField === col) {
                                  setSortDirection((current) =>
                                    current === "asc" ? "desc" : "asc",
                                  );
                                } else {
                                  setSortField(col);
                                  setSortDirection("asc");
                                }
                              }
                            : undefined
                        }
                        role={sortableFieldSet.has(col) ? "button" : undefined}
                        tabIndex={sortableFieldSet.has(col) ? 0 : undefined}
                        onKeyDown={
                          sortableFieldSet.has(col)
                            ? (event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  if (sortField === col) {
                                    setSortDirection((current) =>
                                      current === "asc" ? "desc" : "asc",
                                    );
                                  } else {
                                    setSortField(col);
                                    setSortDirection("asc");
                                  }
                                }
                              }
                            : undefined
                        }
                      >
                        <span className="report-table__drag-handle" aria-hidden="true">
                          {disableColumnLayout ? null : isFrozenColumn ? "▣" : "⋮⋮"}
                        </span>
                        <span className="report-table__column-label-text" title={label}>
                          {label}
                        </span>
                        {sortableFieldSet.has(col) ? (
                          <i
                            className={`fas fa-sort${
                              sortField === col
                                ? sortDirection === "asc"
                                  ? "-up"
                                  : "-down"
                                : ""
                            } report-table__sort-icon${
                              sortField === col ? " is-active" : ""
                            }`}
                            aria-hidden="true"
                          />
                        ) : null}
                      </span>
                      )}
                      {isCompactHeader || hideColumnHeaderActions ? null : (
                        <div className="report-table__column-actions">
                          {!hideAddColumn && !disableColumnLayout ? (
                            <button
                              type="button"
                              className="is-add"
                              title={`Add column after ${label}`}
                              data-tour={
                                tourIds?.addColumn && col === tourIds?.addColumnField
                                  ? tourIds.addColumn
                                  : undefined
                              }
                              onClick={(event) => openColumnsAt(index + 1, event)}
                              aria-label={`Add column after ${label}`}
                            >
                              +
                            </button>
                          ) : null}
                          {showUnfreezeColumn ? (
                            <button
                              type="button"
                              className="is-freeze"
                              title={`Unfreeze ${label}`}
                              data-tour={
                                tourIds?.unfreezeColumn && col === tourIds?.unfreezeColumnField
                                  ? tourIds.unfreezeColumn
                                  : undefined
                              }
                              onClick={() => unfreezeColumn(col)}
                              aria-label={`Unfreeze ${label}`}
                            >
                              <i className="fas fa-unlock" aria-hidden="true" />
                            </button>
                          ) : null}
                          {showUnpinColumn ? (
                            <button
                              type="button"
                              className="is-freeze"
                              title={`Unpin ${label}`}
                              onClick={() =>
                                setUnpinnedRightColumns((prev) =>
                                  prev.includes(col) ? prev : [...prev, col],
                                )
                              }
                              aria-label={`Unpin ${label}`}
                            >
                              <i className="fas fa-unlock" aria-hidden="true" />
                            </button>
                          ) : null}
                          {showFreezeColumn ? (
                            <button
                              type="button"
                              className="is-freeze"
                              title={`Freeze ${label}`}
                              data-tour={
                                tourIds?.freezeColumn && col === tourIds?.freezeColumnField
                                  ? tourIds.freezeColumn
                                  : undefined
                              }
                              onClick={() => freezeColumn(col)}
                              aria-label={`Freeze ${label}`}
                            >
                              <i className="fas fa-lock" aria-hidden="true" />
                            </button>
                          ) : null}
                          {!disableColumnLayout && !isDefaultColumn ? (
                            <button
                              type="button"
                              className="is-remove"
                              title={`Remove ${label}`}
                              data-tour={
                                tourIds?.removeColumn && col === tourIds?.removeColumnField
                                  ? tourIds.removeColumn
                                  : undefined
                              }
                              onClick={() => removeColumn(col)}
                              aria-label={`Remove ${label}`}
                            >
                              ×
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>
                    {isCompactHeader ? (
                      <div className="report-table__column-filter-slot is-compact" aria-hidden="true" />
                    ) : (
                      renderSearchControl(col)
                    )}
                  </th>
                );
              })}
            </tr>
            </thead>
          </table>
        </div>

        <div
          className={`report-table__body-pane${
            showSkeletonRows ? " is-loading" : pageRows.length ? "" : " is-empty"
          }`}
          ref={bodyPaneRef}
          onScroll={syncHeadHorizontalScroll}
          aria-busy={isTableLoading}
          style={
            showSkeletonRows
              ? { "--skeleton-row-count": skeletonRowCount }
              : undefined
          }
        >
          <table
            ref={bodyTableRef}
            className="table table-sm report-table__table report-table__table--body"
          >
            {renderSplitColGroup()}
            <tbody>
            {hasRowSelection && rowSelection?.hint ? (
              <tr className="report-table__selection-hint-row">
                <td colSpan={detailedColumns.length + 1}>{rowSelection.hint}</td>
              </tr>
            ) : null}
            {showSkeletonRows
              ? Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
                  <tr
                    key={`skeleton-${rowIndex}`}
                    className="report-table__skeleton-row"
                  >
                    {hasRowSelection ? (
                      <td
                        className="report-table__selection-column is-frozen"
                        style={selectionStickyStyle}
                      >
                        <span
                          className="report-table__skeleton-bar report-table__skeleton-bar--check"
                          aria-hidden="true"
                        />
                      </td>
                    ) : null}
                    {detailedColumns.map((col, colIndex) => {
                      const frozenIndex = frozenColumns.indexOf(col);
                      const rightFrozenIndex = pinnedRightInView.indexOf(col);
                      const isPinnedRight = rightFrozenIndex >= 0;
                      const frozenStyle = buildFrozenStickyStyle(
                        col,
                        frozenIndex,
                        rightFrozenIndex,
                        isPinnedRight,
                      );
                      const cellClass = [
                        frozenIndex >= 0 ? "is-frozen" : "",
                        isPinnedRight ? "is-pinned-right" : "",
                        resolveColumnClassName(
                          col,
                          getColumnCellClassName?.(col),
                        ),
                      ]
                        .filter(Boolean)
                        .join(" ");
                      const isActions = isReportTableActionsColumn(col);
                      return (
                        <td
                          key={`${col}-skeleton-${rowIndex}`}
                          data-field={col}
                          className={cellClass || undefined}
                          style={frozenStyle}
                        >
                          <span
                            className={`report-table__skeleton-bar${
                              isActions
                                ? " report-table__skeleton-bar--action"
                                : ""
                            }`}
                            style={{
                              width: isActions
                                ? "1.15rem"
                                : `${skeletonBarPercent(rowIndex, colIndex)}%`,
                            }}
                            aria-hidden="true"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))
              : pageRows.length ? pageRows.map((row, rowIndex) => {
              const rowKey = resolveRowKey(row, rowIndex);
              const isSelected =
                selectedRowKey !== "" &&
                selectedRowKey !== undefined &&
                selectedRowKey !== null &&
                String(selectedRowKey) === rowKey;
              return (
              <tr
                key={rowKey}
                className={[
                  isSelected ? "is-selected" : "",
                  onRowClick ? "is-clickable" : "",
                  typeof getRowClassName === "function"
                    ? getRowClassName(row, rowIndex) || ""
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={
                  onRowClick
                    ? () => onRowClick(row, rowIndex)
                    : undefined
                }
              >
                {hasRowSelection ? (
                  <td
                    className="report-table__selection-column is-frozen"
                    style={selectionStickyStyle}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input report-table__row-select"
                      checked={selectedKeySet.has(getRowSelectionKey(row))}
                      disabled={!isRowSelectable(row)}
                      onChange={() => rowSelection.onToggleRow?.(row)}
                      aria-label={`Select row ${getRowSelectionKey(row)}`}
                    />
                  </td>
                ) : null}
                {detailedColumns.map((col) => {
                  const frozenIndex = frozenColumns.indexOf(col);
                  const rightFrozenIndex = pinnedRightInView.indexOf(col);
                  const isPinnedRight = rightFrozenIndex >= 0;
                  const frozenStyle = buildFrozenStickyStyle(
                    col,
                    frozenIndex,
                    rightFrozenIndex,
                    isPinnedRight,
                  );
                  const cellClass = [
                    frozenIndex >= 0 ? "is-frozen" : "",
                    isPinnedRight ? "is-pinned-right" : "",
                    resolveColumnClassName(col, getColumnCellClassName?.(col)),
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <td
                      key={`${col}-${rowIndex}`}
                      data-field={col}
                      className={cellClass || undefined}
                      style={frozenStyle}
                    >
                      {renderTableCell(row, col, rowIndex)}
                    </td>
                  );
                })}
              </tr>
            );
            }) : (
              <tr className="report-table__empty-row">
                <td
                  colSpan={detailedColumns.length + (hasRowSelection ? 1 : 0)}
                  className="report-table__empty-cell"
                >
                  <div className="report-table__empty" role="status">
                    <div className="report-table__empty-icon" aria-hidden="true">
                      <i className="fas fa-inbox" />
                    </div>
                    <strong>{emptyMessage}</strong>
                    {emptyDescription ? <p>{emptyDescription}</p> : null}
                  </div>
                </td>
              </tr>
            )}
            </tbody>
          </table>
        </div>
      </div>

      {(entryTotal > 0 || isTableLoading) ? (
      <div className="report-table__footer" data-tour={tourIds?.footer || undefined}>
        <div className="report-table__footer-left">
          <div className="report-table__count">
            {isTableLoading && entryTotal === 0
              ? "Loading…"
              : `Showing ${entryStart}-${entryEnd} of ${entryTotal} entries${
                  isTableLoading ? " · Loading…" : ""
                }`}
          </div>
        </div>

        <div className="report-table__footer-center">
          {onExportExcel && (
            <button
              type="button"
              className="report-table__export"
              data-tour={tourIds?.export || undefined}
              onClick={() => onExportExcel({ rows: filteredRows, columns: detailedColumns })}
              disabled={!sortedFilteredRows.length || isTableLoading}
            >
              <i className="fas fa-file-excel" aria-hidden="true" />
              {exportLabel}
            </button>
          )}
          {persistLayoutKey ? (
            <button
              type="button"
              className="report-table__reset-layout"
              data-tour={tourIds?.resetLayout || undefined}
              onClick={resetLayout}
              title="Reset columns, filters, sort, and rows-per-page to defaults"
            >
              <i className="fas fa-rotate-left" aria-hidden="true" />
              Reset layout
            </button>
          ) : null}
          {!isServerPaged && (
            <label className="report-table__page-size">
              <span>Rows</span>
              <div className="report-table__page-size-select">
                <select
                  value={
                    pageSizeOptions.includes(tablePageSize)
                      ? tablePageSize
                      : 10
                  }
                  onChange={(event) => {
                    const next = event.target.value;
                    setTablePageSize(next === "all" ? "all" : Number(next));
                  }}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={String(size)} value={size}>
                      {size === "all" ? "All" : size}
                    </option>
                  ))}
                </select>
              </div>
            </label>
          )}
          {isServerPaged && serverPagination?.onPageSizeChange && (
            <label className="report-table__page-size">
              <span>Rows</span>
              <div className="report-table__page-size-select">
                <select
                  value={
                    NUMERIC_PAGE_SIZES.includes(Number(serverPageSize))
                      ? Number(serverPageSize)
                      : "all"
                  }
                  onChange={(event) => {
                    const next = event.target.value;
                    if (next === "all") {
                      serverPagination.onPageSizeChange(
                        Math.max(Number(serverTotalCount) || 10000, 1),
                      );
                      return;
                    }
                    serverPagination.onPageSizeChange(Number(next));
                  }}
                  disabled={isTableLoading}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={String(size)} value={size}>
                      {size === "all" ? "All" : size}
                    </option>
                  ))}
                </select>
              </div>
            </label>
          )}
        </div>

        <div className="report-table__footer-right">
          <div className="report-table__pagination">
            <button
              type="button"
              disabled={activePage <= 1 || isTableLoading}
              onClick={() =>
                isServerPaged
                  ? serverPagination?.onPageChange?.(activePage - 1)
                  : setTablePage((p) => Math.max(1, p - 1))
              }
            >
              Previous
            </button>
            <span>Page {activePage} of {totalPages}</span>
            <button
              type="button"
              disabled={activePage >= totalPages || isTableLoading}
              onClick={() =>
                isServerPaged
                  ? serverPagination?.onPageChange?.(activePage + 1)
                  : setTablePage((p) => Math.min(totalPages, p + 1))
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>
      ) : null}

      {columnPickerIndex != null && (
        <div
          className="report-table__column-popover"
          ref={columnPickerRef}
          style={{
            top: `${columnPickerPosition.top}px`,
            left: `${columnPickerPosition.left}px`,
          }}
        >
          <div className="report-table__column-popover-head">
            <div>
              <strong>Add column</strong>
              <span>Select one to insert here</span>
            </div>
            <button type="button" onClick={() => setColumnPickerIndex(null)} aria-label="Close column picker">
              ×
            </button>
          </div>
          <div className="report-table__column-popover-list">
            {resolvedFieldOptions.map((field) => {
              const alreadySelected = detailedColumns.includes(field.key);
              return (
                <button
                  key={field.key}
                  type="button"
                  disabled={alreadySelected}
                  onClick={() => addColumn(field.key, columnPickerIndex)}
                >
                  <span>{field.label}</span>
                  {alreadySelected ? <small>Added</small> : null}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportTable;
